/** Procurement Overview (Demo Mode only) - "control tower" landing dashboard for the
 * Procurement module: supplier, catalogue, pricing and site-readiness exceptions across the
 * client account / site group / site hierarchy. No backend procurement-exceptions feed exists
 * yet - all figures are static mock data, matching the same "seeded illustrative numbers"
 * approach used by src/pages/ProcurementRollup and src/pages/EstateManagement/EstateOverview.
 * Links into the existing real Suppliers/Catalogues/Products list screens rather than
 * duplicating that data. */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  SvgIcon,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  useTheme,
} from '@mui/material';
import {
  AttachMoney,
  DescriptionOutlined,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  GppMaybe,
  HourglassTop,
  MenuBook,
  PendingActions,
  PeopleAlt,
  RemoveShoppingCart,
  ReportProblem,
  Storefront,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import ExceptionSeverityChip from '../../../components/shared/procurement/ExceptionSeverityChip';
import {
  CLIENT_ACCOUNTS,
  ExceptionSeverity,
  SITES,
  SITE_GROUPS,
  SUPPLIER_CATEGORIES,
  SUPPLIER_STATUSES,
  catalogueExceptions,
  pendingPriceChanges,
  siteReadiness,
  supplierExceptions,
  unavailableProducts,
} from '../../../mocks/data/procurementOverviewData';

const ALL = 'All';

const TILE_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f'];

enum ProcurementOverviewTable {
  SupplierExceptions = 'supplier-exceptions',
  CatalogueExceptions = 'catalogue-exceptions',
  PendingPriceChanges = 'pending-price-changes',
  UnavailableProducts = 'unavailable-products',
  SiteReadiness = 'site-readiness',
}

const ProcurementOverview = () => {
  const { t } = useTranslation('procurementOverview');
  const navigate = useNavigate();
  const theme = useTheme();
  const [clientAccount, setClientAccount] = useState<string>(ALL);
  const [siteGroup, setSiteGroup] = useState<string>(ALL);
  const [site, setSite] = useState<string>(ALL);
  const [supplierCategory, setSupplierCategory] = useState<string>(ALL);
  const [supplierStatus, setSupplierStatus] = useState<string>(ALL);
  const [severity, setSeverity] = useState<ExceptionSeverity | typeof ALL>(ALL);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [activeTable, setActiveTable] = useState<ProcurementOverviewTable>(ProcurementOverviewTable.SupplierExceptions);

  const siteGroupOptions = useMemo(
    () => (clientAccount === ALL
      ? Object.values(SITE_GROUPS).flat()
      : SITE_GROUPS[clientAccount] ?? []),
    [clientAccount],
  );

  const siteOptions = useMemo(
    () => SITES.filter((s) => (clientAccount === ALL || s.clientAccount === clientAccount)
      && (siteGroup === ALL || s.siteGroup === siteGroup)),
    [clientAccount, siteGroup],
  );

  const handleClientAccountChange = (value: string) => {
    setClientAccount(value);
    setSiteGroup(ALL);
    setSite(ALL);
  };

  const handleSiteGroupChange = (value: string) => {
    setSiteGroup(value);
    setSite(ALL);
  };

  const handleClearFilters = () => {
    setClientAccount(ALL);
    setSiteGroup(ALL);
    setSite(ALL);
    setSupplierCategory(ALL);
    setSupplierStatus(ALL);
    setSeverity(ALL);
  };

  const matchesLocation = (row: { clientAccount: string; siteGroup: string }, siteCode?: string) => (clientAccount === ALL || row.clientAccount === clientAccount)
    && (siteGroup === ALL || row.siteGroup === siteGroup)
    && (site === ALL || siteCode === undefined || siteCode === site);

  const matchesSeverity = (row: { severity: ExceptionSeverity }) => severity === ALL || row.severity === severity;

  const filteredSupplierExceptions = useMemo(
    () => supplierExceptions.filter((row) => matchesLocation(row)
      && matchesSeverity(row)
      && (supplierCategory === ALL || row.category === supplierCategory)
      && (supplierStatus === ALL || row.status === supplierStatus)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientAccount, siteGroup, site, supplierCategory, supplierStatus, severity],
  );

  const filteredCatalogueExceptions = useMemo(
    () => catalogueExceptions.filter((row) => matchesLocation(row) && matchesSeverity(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientAccount, siteGroup, site, severity],
  );

  const filteredPendingPriceChanges = useMemo(
    () => pendingPriceChanges.filter((row) => matchesLocation(row) && matchesSeverity(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientAccount, siteGroup, site, severity],
  );

  const filteredUnavailableProducts = useMemo(
    () => unavailableProducts.filter((row) => matchesLocation(row) && matchesSeverity(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientAccount, siteGroup, site, severity],
  );

  const filteredSiteReadiness = useMemo(
    () => siteReadiness.filter((row) => matchesLocation(row, row.siteCode) && matchesSeverity(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientAccount, siteGroup, site, severity],
  );

  const activeSuppliers = filteredSupplierExceptions.filter((row) => row.status === 'Active').length;
  const pendingSupplierRequests = filteredSupplierExceptions.filter((row) => row.status === 'Pending Approval' || row.status === 'Onboarding').length;
  const suppliersMissingDocuments = filteredSupplierExceptions.filter((row) => row.missingDocuments).length;
  const expiredCertifications = filteredSupplierExceptions.filter((row) => row.expiredCertification).length;
  const liveCatalogues = filteredCatalogueExceptions.filter((row) => row.status === 'Live').length;
  const cataloguesAwaitingApproval = filteredCatalogueExceptions.filter((row) => row.status === 'Awaiting Approval').length;
  const catalogueDataIssues = filteredCatalogueExceptions.filter((row) => row.status === 'Data Issue').length;
  const sitesMissingSetup = filteredSiteReadiness.filter((row) => row.readiness === 'Not Ready').length;

  const TILES = [
    { key: 'activeSuppliers', label: t('tileActiveSuppliers', 'Active suppliers'), value: activeSuppliers, subtitle: t('tileActiveSuppliersSubtitle', 'Currently active on account'), icon: <PeopleAlt /> },
    { key: 'pendingSupplierRequests', label: t('tilePendingSupplierRequests', 'Pending supplier requests'), value: pendingSupplierRequests, subtitle: t('tilePendingSupplierRequestsSubtitle', 'Awaiting approval or onboarding'), icon: <HourglassTop /> },
    { key: 'suppliersMissingDocuments', label: t('tileSuppliersMissingDocuments', 'Suppliers missing documents'), value: suppliersMissingDocuments, subtitle: t('tileSuppliersMissingDocumentsSubtitle', 'Compliance documents outstanding'), icon: <DescriptionOutlined /> },
    { key: 'expiredCertifications', label: t('tileExpiredCertifications', 'Expired certifications'), value: expiredCertifications, subtitle: t('tileExpiredCertificationsSubtitle', 'Certifications past expiry'), icon: <GppMaybe /> },
    { key: 'liveCatalogues', label: t('tileLiveCatalogues', 'Live catalogues'), value: liveCatalogues, subtitle: t('tileLiveCataloguesSubtitle', 'Published and active'), icon: <MenuBook /> },
    { key: 'cataloguesAwaitingApproval', label: t('tileCataloguesAwaitingApproval', 'Catalogues awaiting approval'), value: cataloguesAwaitingApproval, subtitle: t('tileCataloguesAwaitingApprovalSubtitle', 'Pending governance review'), icon: <PendingActions /> },
    { key: 'pendingPriceChanges', label: t('tilePendingPriceChanges', 'Pending price changes'), value: filteredPendingPriceChanges.length, subtitle: t('tilePendingPriceChangesSubtitle', 'Submitted, not yet actioned'), icon: <AttachMoney /> },
    { key: 'unavailableProducts', label: t('tileUnavailableProducts', 'Unavailable products'), value: filteredUnavailableProducts.length, subtitle: t('tileUnavailableProductsSubtitle', 'Critical products unavailable'), icon: <RemoveShoppingCart /> },
    { key: 'catalogueDataIssues', label: t('tileCatalogueDataIssues', 'Catalogue data issues'), value: catalogueDataIssues, subtitle: t('tileCatalogueDataIssuesSubtitle', 'Data quality problems flagged'), icon: <ReportProblem /> },
    { key: 'sitesMissingSetup', label: t('tileSitesMissingSetup', 'Sites missing procurement setup'), value: sitesMissingSetup, subtitle: t('tileSitesMissingSetupSubtitle', 'Not yet procurement-ready'), icon: <Storefront /> },
  ];

  return (
    <PageArea>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <PageTitle>{t('pageTitle', 'Procurement Overview')}</PageTitle>
      </Box>

      <Grid container
        spacing={2}
        sx={{ mb: 4 }}>
        {TILES.map((tile, index) => (
          <Grid item
            key={tile.key}
            xs={6}
            md={3}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{
                  backgroundColor: `${TILE_COLORS[index % TILE_COLORS.length]}20`,
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: TILE_COLORS[index % TILE_COLORS.length],
                }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      {/* Quick filters + search row */}
      <Grid container
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}>
        <Grid item>
          <Grid container
            spacing={1}
            alignItems="center">
            <Grid item>
              <Chip
                label={t('severityCritical', 'Critical')}
                color={severity === 'Critical' ? 'primary' : 'default'}
                onClick={() => setSeverity((prev) => (prev === 'Critical' ? ALL : 'Critical'))}
              />
            </Grid>
            <Grid item>
              <Chip
                label={t('severityWarning', 'Warning')}
                color={severity === 'Warning' ? 'primary' : 'default'}
                onClick={() => setSeverity((prev) => (prev === 'Warning' ? ALL : 'Warning'))}
              />
            </Grid>
            <Grid item>
              <Chip
                label={t('severityInfo', 'Info')}
                color={severity === 'Info' ? 'primary' : 'default'}
                onClick={() => setSeverity((prev) => (prev === 'Info' ? ALL : 'Info'))}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Advanced filters accordion */}
      <Accordion
        variant="borderless"
        expanded={isAdvancedOpen}
        onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
      >
        <AccordionSummary>
          <Grid container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Grid item>
              <Grid container
                spacing={1}
                alignItems="center">
                <Grid item>
                  {t('advancedFilters', 'Advanced filters')}
                </Grid>
                <Grid item
                  style={{ marginTop: '8px' }}>
                  {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                size="md"
                variant="secondary"
                onClick={handleClearFilters}>
                {t('clearFilters', 'Clear filters')}
              </Button>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container
            spacing={3}>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('filterClientAccount', 'Client account')}
                value={clientAccount}
                onChange={(e) => handleClientAccountChange(e.target.value)}
              >
                <MenuItem value={ALL}>{t('filterAllClientAccounts', 'All client accounts')}</MenuItem>
                {CLIENT_ACCOUNTS.map((account) => (
                  <MenuItem key={account}
                    value={account}>{account}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('filterSiteGroup', 'Site group')}
                value={siteGroup}
                onChange={(e) => handleSiteGroupChange(e.target.value)}
              >
                <MenuItem value={ALL}>{t('filterAllSiteGroups', 'All site groups')}</MenuItem>
                {siteGroupOptions.map((group) => (
                  <MenuItem key={group}
                    value={group}>{group}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('filterSite', 'Site')}
                value={site}
                onChange={(e) => setSite(e.target.value)}
              >
                <MenuItem value={ALL}>{t('filterAllSites', 'All sites')}</MenuItem>
                {siteOptions.map((s) => (
                  <MenuItem key={s.code}
                    value={s.code}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('filterSupplierCategory', 'Supplier category')}
                value={supplierCategory}
                onChange={(e) => setSupplierCategory(e.target.value)}
              >
                <MenuItem value={ALL}>{t('filterAllCategories', 'All categories')}</MenuItem>
                {SUPPLIER_CATEGORIES.map((category) => (
                  <MenuItem key={category}
                    value={category}>{category}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('filterSupplierStatus', 'Supplier status')}
                value={supplierStatus}
                onChange={(e) => setSupplierStatus(e.target.value)}
              >
                <MenuItem value={ALL}>{t('filterAllStatuses', 'All statuses')}</MenuItem>
                {SUPPLIER_STATUSES.map((status) => (
                  <MenuItem key={status}
                    value={status}>{status}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('filterExceptionSeverity', 'Exception severity')}
                value={severity}
                onChange={(e) => setSeverity(e.target.value as ExceptionSeverity | typeof ALL)}
              >
                <MenuItem value={ALL}>{t('filterAllSeverities', 'All severities')}</MenuItem>
                <MenuItem value="Critical">{t('severityCritical', 'Critical')}</MenuItem>
                <MenuItem value="Warning">{t('severityWarning', 'Warning')}</MenuItem>
                <MenuItem value="Info">{t('severityInfo', 'Info')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Tabs
        value={activeTable}
        onChange={(_, v) => setActiveTable(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mt: 6,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 0,
          '& .MuiTab-root': { minWidth: 'auto', minHeight: 0, px: 2, py: 1.5 },
        }}>
        <Tab label={t('tabSupplierExceptions', 'Supplier exceptions ({{count}})', { count: filteredSupplierExceptions.length })}
          value={ProcurementOverviewTable.SupplierExceptions} />
        <Tab label={t('tabCatalogueExceptions', 'Catalogue exceptions ({{count}})', { count: filteredCatalogueExceptions.length })}
          value={ProcurementOverviewTable.CatalogueExceptions} />
        <Tab label={t('tabPendingPriceChanges', 'Pending price changes ({{count}})', { count: filteredPendingPriceChanges.length })}
          value={ProcurementOverviewTable.PendingPriceChanges} />
        <Tab label={t('tabUnavailableProducts', 'Unavailable critical products ({{count}})', { count: filteredUnavailableProducts.length })}
          value={ProcurementOverviewTable.UnavailableProducts} />
        <Tab label={t('tabSiteReadiness', 'Site procurement readiness ({{count}})', { count: filteredSiteReadiness.length })}
          value={ProcurementOverviewTable.SiteReadiness} />
      </Tabs>

      {activeTable === ProcurementOverviewTable.SupplierExceptions && (
        <Box sx={{ mt: 6 }}>
          <Section>
            <TableHeader>
              {t('supplierExceptionsTitle', 'Supplier exceptions')}
              <Pill text={t('supplierExceptionsCount', '{{count}} exceptions', { count: filteredSupplierExceptions.length })} />
              <Box sx={{ ml: 'auto' }}>
                <Button variant="secondary"
                  onClick={() => navigate('/procurement/suppliers')}>
                  {t('viewSuppliers', 'View Suppliers')}
                </Button>
              </Box>
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columnSupplier', 'Supplier')}</TableCell>
                    <TableCell>{t('columnCategory', 'Category')}</TableCell>
                    <TableCell>{t('columnStatus', 'Status')}</TableCell>
                    <TableCell>{t('columnClientAccount', 'Client account')}</TableCell>
                    <TableCell>{t('columnSiteGroup', 'Site group')}</TableCell>
                    <TableCell>{t('columnIssue', 'Issue')}</TableCell>
                    <TableCell>{t('columnSeverity', 'Severity')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSupplierExceptions.length === 0 && (
                    <TableRow><TableCell colSpan={7}>{t('noSupplierExceptions', 'No supplier exceptions match this filter.')}</TableCell></TableRow>
                  )}
                  {filteredSupplierExceptions.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.supplierName}</TableCell>
                      <TableCell>{row.category}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.clientAccount}</TableCell>
                      <TableCell>{row.siteGroup}</TableCell>
                      <TableCell>{row.issue}</TableCell>
                      <TableCell><ExceptionSeverityChip severity={row.severity} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Box>
      )}


      {activeTable === ProcurementOverviewTable.CatalogueExceptions && (
        <Box sx={{ mt: 6 }}>
          <Section>
            <TableHeader>
              {t('catalogueExceptionsTitle', 'Catalogue exceptions')}
              <Pill text={t('catalogueExceptionsCount', '{{count}} exceptions', { count: filteredCatalogueExceptions.length })} />
              <Box sx={{ ml: 'auto' }}>
                <Button variant="secondary"
                  onClick={() => navigate('/procurement/catalogue-governance')}>
                  {t('viewCatalogueGovernance', 'View Catalogue Governance')}
                </Button>
              </Box>
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columnCatalogue', 'Catalogue')}</TableCell>
                    <TableCell>{t('columnItem', 'Item')}</TableCell>
                    <TableCell>{t('columnStatus', 'Status')}</TableCell>
                    <TableCell>{t('columnClientAccount', 'Client account')}</TableCell>
                    <TableCell>{t('columnSiteGroup', 'Site group')}</TableCell>
                    <TableCell>{t('columnIssue', 'Issue')}</TableCell>
                    <TableCell>{t('columnSeverity', 'Severity')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCatalogueExceptions.length === 0 && (
                    <TableRow><TableCell colSpan={7}>{t('noCatalogueExceptions', 'No catalogue exceptions match this filter.')}</TableCell></TableRow>
                  )}
                  {filteredCatalogueExceptions.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.catalogueName}</TableCell>
                      <TableCell>{row.itemNumber} - {row.itemName}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.clientAccount}</TableCell>
                      <TableCell>{row.siteGroup}</TableCell>
                      <TableCell>{row.issue}</TableCell>
                      <TableCell><ExceptionSeverityChip severity={row.severity} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Box>
      )}

      {activeTable === ProcurementOverviewTable.PendingPriceChanges && (
        <Box sx={{ mt: 6 }}>
          <Section>
            <TableHeader>
              {t('pendingPriceChangesTitle', 'Pending price changes')}
              <Pill text={t('pendingPriceChangesCount', '{{count}} changes', { count: filteredPendingPriceChanges.length })} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columnItem', 'Item')}</TableCell>
                    <TableCell>{t('columnSupplier', 'Supplier')}</TableCell>
                    <TableCell>{t('columnClientAccount', 'Client account')}</TableCell>
                    <TableCell>{t('columnCurrentPrice', 'Current price')}</TableCell>
                    <TableCell>{t('columnProposedPrice', 'Proposed price')}</TableCell>
                    <TableCell>{t('columnSubmitted', 'Submitted')}</TableCell>
                    <TableCell>{t('columnSeverity', 'Severity')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPendingPriceChanges.length === 0 && (
                    <TableRow><TableCell colSpan={7}>{t('noPendingPriceChanges', 'No pending price changes match this filter.')}</TableCell></TableRow>
                  )}
                  {filteredPendingPriceChanges.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.itemNumber} - {row.itemName}</TableCell>
                      <TableCell>{row.supplierName}</TableCell>
                      <TableCell>{row.clientAccount}</TableCell>
                      <TableCell>{row.currentPrice.toFixed(2)}</TableCell>
                      <TableCell>{row.proposedPrice.toFixed(2)}</TableCell>
                      <TableCell>{row.submittedDate}</TableCell>
                      <TableCell><ExceptionSeverityChip severity={row.severity} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Box>
      )}

      {activeTable === ProcurementOverviewTable.UnavailableProducts && (
        <Box sx={{ mt: 6 }}>
          <Section>
            <TableHeader>
              {t('unavailableProductsTitle', 'Unavailable critical products')}
              <Pill text={t('unavailableProductsCount', '{{count}} products', { count: filteredUnavailableProducts.length })} />
              <Box sx={{ ml: 'auto' }}>
                <Button variant="secondary"
                  onClick={() => navigate('/procurement/products')}>
                  {t('viewProducts', 'View Products')}
                </Button>
              </Box>
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columnItem', 'Item')}</TableCell>
                    <TableCell>{t('columnSupplier', 'Supplier')}</TableCell>
                    <TableCell>{t('columnClientAccount', 'Client account')}</TableCell>
                    <TableCell>{t('columnReason', 'Reason')}</TableCell>
                    <TableCell>{t('columnSitesAffected', 'Sites affected')}</TableCell>
                    <TableCell>{t('columnSeverity', 'Severity')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUnavailableProducts.length === 0 && (
                    <TableRow><TableCell colSpan={6}>{t('noUnavailableProducts', 'No unavailable products match this filter.')}</TableCell></TableRow>
                  )}
                  {filteredUnavailableProducts.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.itemNumber} - {row.itemName}</TableCell>
                      <TableCell>{row.supplierName}</TableCell>
                      <TableCell>{row.clientAccount}</TableCell>
                      <TableCell>{row.reason}</TableCell>
                      <TableCell>{row.sitesAffected}</TableCell>
                      <TableCell><ExceptionSeverityChip severity={row.severity} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Box>
      )}

      {activeTable === ProcurementOverviewTable.SiteReadiness && (
        <Box sx={{ mt: 6 }}>
          <Section>
            <TableHeader>
              {t('siteReadinessTitle', 'Site procurement readiness')}
              <Pill text={t('siteReadinessCount', '{{count}} sites', { count: filteredSiteReadiness.length })} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columnSite', 'Site')}</TableCell>
                    <TableCell>{t('columnClientAccount', 'Client account')}</TableCell>
                    <TableCell>{t('columnSiteGroup', 'Site group')}</TableCell>
                    <TableCell>{t('columnSuppliersLinked', 'Suppliers linked')}</TableCell>
                    <TableCell>{t('columnCataloguesAssigned', 'Catalogues assigned')}</TableCell>
                    <TableCell>{t('columnOpenExceptions', 'Open exceptions')}</TableCell>
                    <TableCell>{t('columnReadiness', 'Readiness')}</TableCell>
                    <TableCell>{t('columnSeverity', 'Severity')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSiteReadiness.length === 0 && (
                    <TableRow><TableCell colSpan={8}>{t('noSitesMatch', 'No sites match this filter.')}</TableCell></TableRow>
                  )}
                  {filteredSiteReadiness.map((row) => (
                    <TableRow key={row.siteCode}>
                      <TableCell>{row.siteName} ({row.siteCode})</TableCell>
                      <TableCell>{row.clientAccount}</TableCell>
                      <TableCell>{row.siteGroup}</TableCell>
                      <TableCell>{row.suppliersLinked}</TableCell>
                      <TableCell>{row.cataloguesAssigned}</TableCell>
                      <TableCell>{row.openExceptions}</TableCell>
                      <TableCell>{row.readiness}</TableCell>
                      <TableCell><ExceptionSeverityChip severity={row.severity} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Box>
      )}
    </PageArea>
  );
};

export default ProcurementOverview;
